import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Search,
  RefreshCw,
  Eye,
  AlertCircle,
  Loader2,
  ChevronDown,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock as ClockIcon,
  Flag,
  AlertTriangle,
  FileText,
  MessageSquare,
  Plus,
  X,
  Calendar as CalendarIcon,
  MapPin,
  Phone,
  Mail,
  User,
  Building,
  Info,
  Hash,
  Tag,
  ClipboardList,
} from "lucide-react";
import { requestService } from "../../api/services/requestService";
import { useToast } from "../../components/common/ThemeToaster";
import { format } from "date-fns";

const UserRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [expandedRequests, setExpandedRequests] = useState(new Set());
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inReview: 0,
    approved: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    rejected: 0,
  });
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, sortBy, sortOrder]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const statusMap = {
        pending: "pending",
        in_review: "contacted",
        approved: "approved",
        in_progress: "quoted",
        completed: "completed",
        cancelled: "cancelled",
        rejected: "rejected",
      };

      let params = {
        sortBy: sortBy,
        sortOrder: sortOrder,
        page: 1,
        limit: 50,
      };

      if (statusFilter !== "all" && statusMap[statusFilter]) {
        params.status = statusMap[statusFilter];
      }

      console.log("Fetching requests with params:", params);
      const response = await requestService.getMyRequests(params);
      console.log("Requests response:", response);

      let requestsData = [];
      let paginationData = null;

      if (response?.requests && Array.isArray(response.requests)) {
        requestsData = response.requests;
        paginationData = response.pagination;
      } else if (
        response?.data?.requests &&
        Array.isArray(response.data.requests)
      ) {
        requestsData = response.data.requests;
        paginationData = response.data.pagination;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        requestsData = response.data.data;
        paginationData = response.data.pagination;
      } else if (Array.isArray(response?.data)) {
        requestsData = response.data;
      } else if (Array.isArray(response)) {
        requestsData = response;
      }

      setRequests(requestsData || []);

      if (requestsData.length > 0) {
        calculateStats(requestsData);
      }
    } catch (err) {
      console.error("Error fetching requests:", err);
      const errorMsg =
        err.response?.data?.message || err.message || "Failed to load requests";
      setError(errorMsg);
      toastError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (requestsList) => {
    const newStats = {
      total: requestsList.length,
      pending: requestsList.filter((r) => r.status === "pending").length,
      inReview: requestsList.filter((r) => r.status === "contacted").length,
      approved: requestsList.filter((r) => r.status === "approved").length,
      inProgress: requestsList.filter((r) => r.status === "quoted").length,
      completed: requestsList.filter((r) => r.status === "completed").length,
      cancelled: requestsList.filter((r) => r.status === "cancelled").length,
      rejected: requestsList.filter((r) => r.status === "rejected").length,
    };
    setStats(newStats);
  };

  const handleCancelRequest = async () => {
    if (!selectedRequest) return;

    try {
      await requestService.cancelRequest(selectedRequest.id, cancelReason);
      toastSuccess("Request cancelled successfully");
      setShowCancelModal(false);
      setCancelReason("");
      setSelectedRequest(null);
      await fetchRequests();
    } catch (err) {
      console.error("Error cancelling request:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Failed to cancel request";
      toastError(errorMsg);
    }
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedRequests);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRequests(newExpanded);
  };

  const parseProducts = (products) => {
    if (!products) return [];
    if (typeof products === "string") {
      try {
        return JSON.parse(products);
      } catch (e) {
        return [];
      }
    }
    if (Array.isArray(products)) {
      return products;
    }
    return [];
  };

  const getStatusBadge = (status) => {
    const statuses = {
      draft: {
        label: "Draft",
        color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
        icon: FileText,
      },
      pending: {
        label: "Pending",
        color:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",
        icon: ClockIcon,
      },
      contacted: {
        label: "Contacted",
        color:
          "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
        icon: Eye,
      },
      quoted: {
        label: "Quoted",
        color:
          "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400",
        icon: FileText,
      },
      approved: {
        label: "Approved",
        color:
          "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        icon: CheckCircle,
      },
      completed: {
        label: "Completed",
        color:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400",
        icon: CheckCircle,
      },
      cancelled: {
        label: "Cancelled",
        color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
        icon: XCircle,
      },
      rejected: {
        label: "Rejected",
        color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
        icon: AlertTriangle,
      },
    };
    return statuses[status] || statuses.pending;
  };

  const getPriorityBadge = (priority) => {
    const priorities = {
      low: {
        label: "Low",
        color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
        icon: Flag,
      },
      medium: {
        label: "Medium",
        color:
          "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
        icon: Flag,
      },
      high: {
        label: "High",
        color:
          "bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400",
        icon: AlertTriangle,
      },
      urgent: {
        label: "Urgent",
        color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
        icon: AlertCircle,
      },
    };
    return priorities[priority?.toLowerCase()] || priorities.medium;
  };

  const formatCurrency = (value) => {
    if (!value) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM d, yyyy h:mm a");
    } catch {
      return "Invalid date";
    }
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <motion.div
      whileHover={{ y: -2, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all"
    >
      <div className="flex items-center justify-between">
        <div
          className={`p-2 rounded-xl bg-${color}-100 dark:bg-${color}-900/20`}
        >
          <Icon
            size={20}
            className={`text-${color}-600 dark:text-${color}-400`}
          />
        </div>
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value}
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{label}</p>
    </motion.div>
  );

  const RequestCard = ({ request }) => {
    const status = getStatusBadge(request.status);
    const priority = getPriorityBadge(request.priority);
    const StatusIcon = status.icon;
    const PriorityIcon = priority.icon;
    const isExpanded = expandedRequests.has(request.id);
    const productsList = parseProducts(request.products);

    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        whileHover={{ y: -2 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
      >
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              {/* Header */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span
                  className={`px-2.5 py-1 text-xs rounded-full flex items-center gap-1 ${status.color}`}
                >
                  <StatusIcon size={10} />
                  {status.label}
                </span>
                <span
                  className={`px-2.5 py-1 text-xs rounded-full flex items-center gap-1 ${priority.color}`}
                >
                  <PriorityIcon size={10} />
                  {priority.label}
                </span>
                <span className="text-xs text-gray-500 font-mono">
                  #{request.requestNumber || request.id}
                </span>
              </div>

              {/* Subject */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {request.subject || "Product Inquiry"}
              </h3>

              {/* Message Preview */}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                {request.message || "No message provided"}
              </p>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-3">
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(request.createdAt)}
                </span>
                {request.estimatedValue && (
                  <span className="flex items-center gap-1">
                    <DollarSign size={12} />
                    {formatCurrency(request.estimatedValue)}
                  </span>
                )}
                {productsList.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Package size={12} />
                    {productsList.length} product(s)
                  </span>
                )}
              </div>

              {/* Expand/Collapse Button */}
              <button
                onClick={() => toggleExpand(request.id)}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {isExpanded ? "Show less" : "Show more"}
                <ChevronDown
                  size={12}
                  className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                />
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 space-y-3"
                  >
                    {/* Full Message */}
                    {request.message && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Message:
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                          {request.message}
                        </p>
                      </div>
                    )}

                    {/* Products/Items */}
                    {productsList.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Products:
                        </p>
                        <div className="space-y-2">
                          {productsList.map((item, idx) => (
                            <div
                              key={idx}
                              className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {item.productName ||
                                      item.name ||
                                      `Product ${idx + 1}`}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Product ID: {item.productId || item.id}
                                  </p>
                                  {item.notes && (
                                    <p className="text-xs text-gray-500 mt-1 italic">
                                      Notes: {item.notes}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Delivery Info */}
                    {request.deliveryAddress && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Delivery Address:
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {request.deliveryAddress}
                          {request.deliveryCity && `, ${request.deliveryCity}`}
                          {request.deliveryCountry &&
                            `, ${request.deliveryCountry}`}
                          {request.deliveryPostalCode &&
                            ` ${request.deliveryPostalCode}`}
                        </p>
                      </div>
                    )}

                    {/* Admin Notes/Response */}
                    {request.adminNotes && (
                      <div className="p-3 bg-primary-50 dark:bg-primary-900/10 rounded-xl">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-1 flex items-center gap-1">
                          <MessageSquare size={12} />
                          Admin Response:
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {request.adminNotes}
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Actions */}
            <div className="flex gap-1">
              <button
                onClick={() => {
                  setSelectedRequest(request);
                  setShowDetailsModal(true);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                title="View Details"
              >
                <Eye size={16} className="text-blue-500" />
              </button>
              {(request.status === "pending" ||
                request.status === "contacted") && (
                <button
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowCancelModal(true);
                  }}
                  className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Cancel Request"
                >
                  <XCircle size={16} className="text-red-500" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const filteredRequests = requests.filter((request) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (request.requestNumber?.toString() || "")
        .toLowerCase()
        .includes(searchLower) ||
      (request.subject || "").toLowerCase().includes(searchLower) ||
      (request.message || "").toLowerCase().includes(searchLower)
    );
  });

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="relative">
            <Loader2
              size={48}
              className="animate-spin text-primary-500 mx-auto mb-4"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Package size={24} className="text-primary-300" />
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium">
            Loading your requests...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            My Requests
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Track and manage your product requests
          </p>
        </div>
        <button
          onClick={() => navigate("/inquiry")}
          className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-medium transition-all shadow-lg shadow-primary-600/20 flex items-center gap-2"
        >
          <Plus size={18} />
          New Request
        </button>
      </div>

      {/* Stats Cards */}
      {stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Package}
            label="Total"
            value={stats.total}
            color="blue"
          />
          <StatCard
            icon={ClockIcon}
            label="Pending"
            value={stats.pending}
            color="yellow"
          />
          <StatCard
            icon={CheckCircle}
            label="Approved"
            value={stats.approved}
            color="green"
          />
          <StatCard
            icon={CheckCircle}
            label="Completed"
            value={stats.completed}
            color="emerald"
          />
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by request number, subject, or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_review">In Review (Contacted)</option>
              <option value="approved">Approved</option>
              <option value="in_progress">In Progress (Quoted)</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
            <button
              onClick={fetchRequests}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl flex items-center gap-3"
          >
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700 dark:text-red-300 text-sm flex-1">
              {error}
            </p>
            <button
              onClick={() => setError(null)}
              className="p-1 hover:bg-red-100 rounded-lg"
            >
              <X size={16} className="text-red-500" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Requests List */}
      <div className="space-y-4">
        <AnimatePresence>
          {filteredRequests.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredRequests.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800"
        >
          <div className="inline-flex p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
            <Package size={48} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No requests found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {searchTerm || statusFilter !== "all"
              ? "No requests match your search criteria. Try adjusting your filters."
              : "You haven't created any requests yet. Start by creating your first request."}
          </p>
          {!searchTerm && statusFilter === "all" && (
            <button
              onClick={() => navigate("/inquiry")}
              className="mt-6 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl inline-flex items-center gap-2 font-medium shadow-sm"
            >
              <Plus size={18} />
              Create Your First Request
            </button>
          )}
        </motion.div>
      )}

      {/* Cancel Modal */}
      <AnimatePresence>
        {showCancelModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-xl">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Cancel Request
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to cancel request #
                {selectedRequest.requestNumber || selectedRequest.id}? This
                action cannot be undone.
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Reason for cancellation (optional)"
                rows={3}
                className="w-full px-4 py-2 mb-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Keep Request
                </button>
                <button
                  onClick={handleCancelRequest}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors"
                >
                  Cancel Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-xl">
                    <FileText className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Request Details
                    </h3>
                    <p className="text-xs text-gray-500">
                      #{selectedRequest.requestNumber || selectedRequest.id}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Status & Priority */}
                <div className="flex flex-wrap gap-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full ${getStatusBadge(selectedRequest.status).color}`}
                  >
                    {React.createElement(
                      getStatusBadge(selectedRequest.status).icon,
                      { size: 14 },
                    )}
                    Status: {getStatusBadge(selectedRequest.status).label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full ${getPriorityBadge(selectedRequest.priority).color}`}
                  >
                    {React.createElement(
                      getPriorityBadge(selectedRequest.priority).icon,
                      { size: 14 },
                    )}
                    Priority: {getPriorityBadge(selectedRequest.priority).label}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                    <Calendar size={14} />
                    Created: {formatDateTime(selectedRequest.createdAt)}
                  </span>
                </div>

                {/* Subject */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Subject
                  </p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {selectedRequest.subject || "Product Inquiry"}
                  </p>
                </div>

                {/* Message */}
                {selectedRequest.message && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      Message
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedRequest.message}
                    </p>
                  </div>
                )}

                {/* Products */}
                {(() => {
                  const productsList = parseProducts(selectedRequest.products);
                  if (productsList.length > 0) {
                    return (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <Package size={16} className="text-primary-500" />
                          Products ({productsList.length})
                        </h4>
                        <div className="space-y-3">
                          {productsList.map((item, idx) => (
                            <div
                              key={idx}
                              className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {item.productName ||
                                    item.name ||
                                    `Product ${idx + 1}`}
                                </p>
                              </div>
                              <p className="text-xs text-gray-500">
                                Product ID: {item.productId || item.id}
                              </p>
                              {item.notes && (
                                <p className="text-xs text-gray-500 mt-2 italic">
                                  <span className="font-medium">Notes:</span>{" "}
                                  {item.notes}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Delivery Information */}
                {(selectedRequest.deliveryAddress ||
                  selectedRequest.deliveryCity ||
                  selectedRequest.deliveryCountry) && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <MapPin size={16} className="text-primary-500" />
                      Delivery Information
                    </h4>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {selectedRequest.deliveryAddress && (
                          <>
                            {selectedRequest.deliveryAddress}
                            <br />
                          </>
                        )}
                        {selectedRequest.deliveryCity && (
                          <>
                            {selectedRequest.deliveryCity}
                            <br />
                          </>
                        )}
                        {selectedRequest.deliveryCountry && (
                          <>
                            {selectedRequest.deliveryCountry}
                            <br />
                          </>
                        )}
                        {selectedRequest.deliveryPostalCode && (
                          <>{selectedRequest.deliveryPostalCode}</>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Admin Response */}
                {selectedRequest.adminNotes && (
                  <div className="p-4 bg-primary-50 dark:bg-primary-900/10 rounded-xl">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-1 flex items-center gap-1">
                      <MessageSquare size={12} />
                      Admin Response
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {selectedRequest.adminNotes}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserRequests;
